import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BRAND, waLink } from "@/lib/constants";
import { BrandedImage } from "@/components/site/BrandedImage";
import {
  Flame, Mic, Laptop, Users, MapPin, Calendar, Clock, Ticket, ArrowRight,
  Check, Quote, Share2, MessageCircle, Facebook, Sparkles, Send, Mountain
} from "lucide-react";

const CATEGORIES = [
  {
    key: "bootcamp", label: "Bootcamp „Nota de Plată”", icon: Mountain,
    badge: "🔥 Transformare profundă",
    color: "from-[#C83A3A] to-[#8A2323]",
    desc: "Experiență intensivă de 3–7 zile pentru reset mental, fizic și emoțional. Disciplină, sport, workshopuri, strategie juridică, terapie de grup.",
  },
  {
    key: "conferinta", label: "Conferințe & Summit-uri", icon: Mic,
    badge: "🎤 Live Speakers",
    color: "from-[#D4A63D] to-[#8A6D1E]",
    desc: "Evenimente live cu psihologi, avocați, experți și părinți care și-au spus povestea. Networking + Q&A.",
  },
  {
    key: "online", label: "Întâlniri Online", icon: Laptop,
    badge: "🌍 Oriunde în lume",
    color: "from-[#5FA0E8] to-[#3F7EC8]",
    desc: "Sesiuni private sau de grup Zoom pentru cei din România și diaspora. Facilitate de psihoterapeuți certificați.",
  },
  {
    key: "support", label: "Grupuri de Suport", icon: Users,
    badge: "🤝 Comunitate",
    color: "from-[#3BA76D] to-[#2D8557]",
    desc: "Întâlniri regulate — fizice sau online — unde poți vorbi cu oameni care te înțeleg. Gratuit, fără presiune.",
  },
];

const STATUS_META = {
  open: { label: "Disponibil", color: "bg-[#3BA76D]/20 text-[#3BA76D] border-[#3BA76D]/40" },
  almost_full: { label: "Aproape plin", color: "bg-[#D4A63D]/20 text-[#D4A63D] border-[#D4A63D]/40" },
  sold_out: { label: "Sold out", color: "bg-[#C83A3A]/20 text-[#C83A3A] border-[#C83A3A]/40" },
  completed: { label: "Încheiat", color: "bg-white/5 text-[#6C7A9C] border-white/10" },
};

const TYPE_LABEL = { bootcamp: "Bootcamp", conferinta: "Conferință", online: "Online", support: "Suport" };

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

export default function Intalniri() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("toate");
  const [testimonials, setTestimonials] = useState([]);
  const [hostForm, setHostForm] = useState({ name: "", email: "", phone: "", city: "", proposed_location: "", estimated_participants: 20, message: "" });
  const [hostSubmitting, setHostSubmitting] = useState(false);

  useEffect(() => {
    api.get("/events", { params: { event_type: filter } }).then((r) => setEvents(r.data || [])).catch(() => {});
  }, [filter]);

  useEffect(() => {
    api.get("/events/testimonials/list").then((r) => setTestimonials(r.data || [])).catch(() => {});
  }, []);

  const submitHost = async (e) => {
    e.preventDefault();
    if (!hostForm.city.trim() || !hostForm.email.trim() || !hostForm.name.trim()) {
      toast.error("Numele, email-ul și orașul sunt obligatorii");
      return;
    }
    setHostSubmitting(true);
    try {
      await api.post("/events/host-request", hostForm);
      toast.success("Mulțumim! Îți vom scrie curând.");
      setHostForm({ name: "", email: "", phone: "", city: "", proposed_location: "", estimated_participants: 20, message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la trimitere");
    } finally {
      setHostSubmitting(false);
    }
  };

  return (
    <div data-testid="page-intalniri" className="text-[#F8F8F4]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <BrandedImage
            src={BRAND.imgSunsetVillage}
            alt="Întâlniri Tati & Sofia"
            className="absolute inset-0 w-full h-full"
            watermarkPosition="br"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1020]/70 via-[#0A1020]/85 to-[#0A1020] pointer-events-none z-[5]" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36 text-center">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8] bg-[#E89BB8]/10 border border-[#E89BB8]/30 px-3 py-1.5 rounded-sm mb-5">
            <Sparkles className="w-3 h-3" /> Bootcamp · Conferințe · Zoom · Suport
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl leading-[1.05] text-[#F8F8F4]">
            ÎNTÂLNIRI <span className="gold-text">TATI & SOFIA</span>
          </h1>
          <p className="mt-5 text-lg md:text-xl text-[#E89BB8] font-quote italic max-w-3xl mx-auto">
            Nu ești singur. Vindecarea începe când ne conectăm.
          </p>
          <p className="mt-4 text-[14px] md:text-[15px] text-[#A0AABF] leading-relaxed max-w-2xl mx-auto">
            Participă la <strong className="text-[#F8F8F4]">bootcamp-uri</strong>, <strong className="text-[#F8F8F4]">conferințe</strong>, <strong className="text-[#F8F8F4]">workshopuri</strong> și <strong className="text-[#F8F8F4]">întâlniri online</strong> dedicate părinților care trec prin alienare parentală, divorț conflictual sau abuz emoțional.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a href="#evenimente" data-testid="hero-cta-view" className="btn-gold rounded-sm px-6 py-3 text-[12px] font-bold tracking-widest inline-flex items-center gap-2">
              <Ticket className="w-4 h-4" /> VEZI EVENIMENTELE
            </a>
            <a href="#booking-carduri" data-testid="hero-cta-book" className="btn-gold-outline rounded-sm px-6 py-3 text-[12px] font-bold tracking-widest">REZERVĂ LOC</a>
            <a href="#host-event" data-testid="hero-cta-host" className="text-[#F8F8F4]/80 hover:text-[#D4A63D] rounded-sm px-4 py-3 text-[12px] font-bold tracking-widest border border-white/20">PROPUNE UN EVENIMENT</a>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <header className="mb-8 text-center max-w-2xl mx-auto">
          <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4A63D]">4 formate · 1 misiune</div>
          <h2 className="font-display text-3xl md:text-4xl text-[#F8F8F4] mt-2">Cum ne întâlnim</h2>
        </header>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const isActive = filter === c.key;
            return (
              <button
                type="button"
                key={c.key}
                data-testid={`category-${c.key}`}
                onClick={() => { setFilter(c.key); document.getElementById("evenimente")?.scrollIntoView({ behavior: "smooth" }); }}
                className={`text-left dense-card rounded-lg p-5 group transition-all hover:border-[#D4A63D]/40 ${isActive ? "ring-2 ring-[#D4A63D] ring-offset-2 ring-offset-[#0A1020]" : ""}`}
              >
                <div className={`w-12 h-12 rounded-sm bg-gradient-to-br ${c.color} grid place-items-center mb-3 text-[#F8F8F4]`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display text-lg text-[#F8F8F4] leading-tight mb-1.5">{c.label}</h3>
                <p className="text-[12.5px] text-[#A0AABF] leading-relaxed line-clamp-4">{c.desc}</p>
                <div className="mt-3 pt-3 border-t border-white/5 text-[10.5px] uppercase tracking-widest text-[#D4A63D] font-semibold">{c.badge}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section id="evenimente" className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8]">Evenimente viitoare</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F8F8F4] mt-2">Alege-ți drumul</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="events-filter-bar">
            {[["toate", "Toate"], ["bootcamp", "Bootcamp"], ["conferinta", "Conferință"], ["online", "Online"], ["support", "Suport"]].map(([v, l]) => (
              <button
                key={v}
                type="button"
                data-testid={`filter-${v}`}
                onClick={() => setFilter(v)}
                className={`text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-sm border transition-colors ${
                  filter === v
                    ? "bg-[#D4A63D] text-[#0A1020] border-[#D4A63D]"
                    : "text-[#F8F8F4]/80 border-white/15 hover:border-[#D4A63D]/50"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </header>

        <div id="booking-carduri" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {events.length === 0 && (
            <div className="col-span-full text-center text-[#A0AABF] py-16">Nu există evenimente pentru filtrul selectat.</div>
          )}
          {events.map((ev) => {
            const status = STATUS_META[ev.status] || STATUS_META.open;
            return (
              <article
                key={ev.event_id}
                data-testid={`event-card-${ev.slug}`}
                className="dense-card rounded-lg overflow-hidden flex flex-col group"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-[#0A1020]">
                  <BrandedImage
                    src={ev.cover_image}
                    alt={ev.title}
                    className="absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-700"
                    watermarkPosition="tr"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/20 to-transparent pointer-events-none z-[5]" />
                  <div className="absolute top-2.5 left-2.5 flex gap-1.5 z-10">
                    <span className="text-[9.5px] font-bold tracking-widest uppercase text-[#D4A63D] bg-[#0A1020]/85 border border-[#D4A63D]/30 px-2 py-1 rounded-sm">
                      {TYPE_LABEL[ev.event_type] || ev.event_type}
                    </span>
                    <span className={`text-[9.5px] font-bold tracking-widest uppercase px-2 py-1 rounded-sm border ${status.color}`}>{status.label}</span>
                  </div>
                </div>

                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-display text-lg text-[#F8F8F4] leading-tight line-clamp-2">{ev.title}</h3>
                  <p className="text-[12.5px] text-[#A0AABF] mt-1.5 leading-relaxed line-clamp-3">{ev.description_short}</p>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-[#A0AABF]">
                    <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-[#D4A63D]" /> {formatDate(ev.start_date)}</div>
                    <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#D4A63D]" /> {ev.location_city || "Online"}</div>
                    <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#D4A63D]" /> {ev.seats_remaining}/{ev.seats_total} locuri</div>
                    <div className="flex items-center gap-1.5">
                      <Ticket className="w-3.5 h-3.5 text-[#D4A63D]" />
                      {ev.price > 0 ? `${ev.price.toFixed(0)} RON` : <span className="text-[#3BA76D] font-bold">Gratuit</span>}
                    </div>
                  </div>

                  {ev.trainer && (
                    <div className="mt-3 text-[11px] text-[#F8F8F4]/85 pt-3 border-t border-white/5">
                      <span className="text-[#6C7A9C] uppercase tracking-widest text-[10px]">Trainer:</span> {ev.trainer}
                    </div>
                  )}

                  <div className="mt-3 flex gap-2 pt-3">
                    <Link
                      to={`/intalniri/${ev.slug}`}
                      data-testid={`event-details-${ev.slug}`}
                      className="flex-1 btn-gold-outline rounded-sm py-2.5 text-[11px] font-bold tracking-widest text-center inline-flex items-center justify-center gap-1.5"
                    >
                      VEZI DETALII
                    </Link>
                    {ev.status !== "sold_out" && ev.status !== "completed" && (
                      <Link
                        to={`/intalniri/${ev.slug}#rezerva`}
                        data-testid={`event-book-${ev.slug}`}
                        className="flex-1 btn-gold rounded-sm py-2.5 text-[11px] font-bold tracking-widest text-center"
                      >
                        REZERVĂ LOC
                      </Link>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* PAST EVENTS TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <header className="mb-8 text-center max-w-2xl mx-auto">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#D4A63D]">Voci din bootcamp</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F8F8F4] mt-2">Așa s-au întors acasă</h2>
          </header>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="testimonials-grid">
            {testimonials.map((t, i) => (
              <blockquote key={t.testimonial_id || i} className="cinematic-card rounded-lg p-5 relative">
                <Quote className="w-6 h-6 text-[#D4A63D]/40 mb-2" />
                <p className="text-[13.5px] italic text-[#F8F8F4]/90 leading-relaxed line-clamp-5">„{t.testimonial}"</p>
                <footer className="mt-4 pt-3 border-t border-white/5 text-[11px] text-[#A0AABF]">
                  <div className="font-semibold text-[#F8F8F4]">{t.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#6C7A9C] mt-0.5">{t.role}</div>
                </footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {/* HOST EVENT */}
      <section id="host-event" className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="dense-card rounded-lg p-6 md:p-10 grid md:grid-cols-2 gap-8 items-start">
          <div>
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#E89BB8]">Adu-ne în orașul tău</div>
            <h2 className="font-display text-3xl md:text-4xl text-[#F8F8F4] mt-2 leading-tight">Vrei să organizăm un eveniment în orașul tău?</h2>
            <p className="mt-4 text-[14px] text-[#A0AABF] leading-relaxed">
              Dacă ești un părinte, un profesionist sau un lider comunitar și vrei să aduci un bootcamp, o conferință sau un grup de suport în zona ta — spune-ne. Ne mișcăm oriunde este nevoie.
            </p>
            <div className="mt-6 space-y-2 text-[13px] text-[#F8F8F4]/85">
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3BA76D]" /> Organizăm noi logistica</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3BA76D]" /> Aducem trainerii și speakerii</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#3BA76D]" /> Tu ne ajuți cu locația și promovarea locală</div>
            </div>
          </div>

          <form onSubmit={submitHost} className="space-y-3" data-testid="host-form">
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="host-name" value={hostForm.name} onChange={(e) => setHostForm({ ...hostForm, name: e.target.value })} placeholder="Nume" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
              <input required type="email" data-testid="host-email" value={hostForm.email} onChange={(e) => setHostForm({ ...hostForm, email: e.target.value })} placeholder="Email" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input required data-testid="host-city" value={hostForm.city} onChange={(e) => setHostForm({ ...hostForm, city: e.target.value })} placeholder="Oraș" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
              <input type="tel" data-testid="host-phone" value={hostForm.phone} onChange={(e) => setHostForm({ ...hostForm, phone: e.target.value })} placeholder="Telefon (opțional)" className="bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
            </div>
            <input data-testid="host-location" value={hostForm.proposed_location} onChange={(e) => setHostForm({ ...hostForm, proposed_location: e.target.value })} placeholder="Locație propusă (ex: Sala Palatului, hotel, cabană)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
            <input type="number" min="5" max="1000" data-testid="host-participants" value={hostForm.estimated_participants} onChange={(e) => setHostForm({ ...hostForm, estimated_participants: parseInt(e.target.value) || 20 })} placeholder="Număr participanți estimat" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none" />
            <textarea rows="4" data-testid="host-message" value={hostForm.message} onChange={(e) => setHostForm({ ...hostForm, message: e.target.value })} placeholder="Mesaj: context, tip de eveniment dorit, buget aproximativ (opțional)" className="w-full bg-[#0A1020] border border-white/10 rounded-sm px-3 py-2.5 text-sm text-[#F8F8F4] focus:border-[#D4A63D] outline-none resize-none" />
            <button type="submit" disabled={hostSubmitting} data-testid="host-submit" className="w-full btn-gold rounded-sm py-3 text-[11px] font-bold tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-50">
              <Send className="w-4 h-4" /> {hostSubmitting ? "SE TRIMITE..." : "TRIMITE CEREREA"}
            </button>
          </form>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={BRAND.heroDreams} alt="Foc de tabără · Munte" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1020]/80 via-[#0A1020]/95 to-[#0A1020] pointer-events-none" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 text-center">
          <h2 className="font-display text-3xl md:text-5xl text-[#F8F8F4] leading-tight">
            Uneori nu ai nevoie de încă un <span className="rose-grad-text">termen în instanță</span>.
          </h2>
          <p className="mt-4 font-quote text-xl md:text-3xl italic text-[#E89BB8]">
            Ai nevoie de oameni care să te ridice.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center" data-testid="cta-final-buttons">
            <a href="#evenimente" className="btn-gold rounded-sm px-6 py-3 text-[12px] font-bold tracking-widest">ÎNSCRIE-TE ACUM</a>
            <Link to="/doneaza" className="btn-gold-outline rounded-sm px-6 py-3 text-[12px] font-bold tracking-widest">DONEAZĂ UN LOC</Link>
            <a href={waLink("Salut, aș vrea să întreb ceva despre întâlnirile Tati & Sofia.")} target="_blank" rel="noreferrer" className="text-[#F8F8F4]/85 hover:text-[#D4A63D] rounded-sm px-4 py-3 text-[12px] font-bold tracking-widest border border-white/20 inline-flex items-center gap-2">
              <MessageCircle className="w-4 h-4" /> ÎNTREABĂ PE WHATSAPP
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
